import { Button } from '@cloudflare/kumo';
import { useMutation } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { dnsValueFields, shortDnsName } from '../../shared/dns-display.ts';
import type { DnsRecord, DomainSummary } from '../../shared/domains.ts';

function CopyField({
  label,
  value,
  type,
  children,
}: {
  label: string;
  value: string;
  type: string;
  children?: ReactNode;
}) {
  const copy = useMutation({ mutationFn: () => navigator.clipboard.writeText(value) });
  return (
    <div className="min-w-0">
      <dt className="text-sm text-kumo-subtle">{label}</dt>
      <dd className="m-0">
        <div className="flex items-start justify-between gap-2">
          <code className="min-w-0 whitespace-pre-wrap wrap-anywhere select-text">{value || '(none)'}</code>
          <Button
            size="sm"
            variant="ghost"
            className="shrink-0"
            aria-label={`Copy ${type} ${label.toLowerCase()}`}
            onClick={() => copy.mutate()}
          >
            {copy.isSuccess ? 'Copied' : 'Copy'}
          </Button>
        </div>
        {copy.isError && (
          <p role="alert" className="text-sm text-kumo-subtle">
            Couldn’t copy. Select and copy the text.
          </p>
        )}
        {children}
      </dd>
    </div>
  );
}

export function DnsRecords({ domain }: { domain: DomainSummary }) {
  if (domain.included) return <p>This address is configured for you. DNS-AID requires a custom domain.</p>;
  const { address, ownership, discovery } = domain.details.records;
  return (
    <div className="grid gap-5 min-w-0">
      <p className="text-sm text-kumo-subtle">
        Use Host / Name when your provider adds your domain automatically. Use @ for the root.
      </p>
      {(address || ownership) && (
        <section className="grid gap-3 min-w-0" aria-label="Required records">
          <h4 className="font-semibold">Required records</h4>
          {address && <DnsRecordCard domain={domain} record={address} />}
          {ownership && <DnsRecordCard domain={domain} record={ownership} />}
        </section>
      )}
      {discovery && (
        <section className="grid gap-3 min-w-0" aria-label="Recommended records">
          <h4 className="font-semibold">Recommended: help agents discover your site</h4>
          <p className="text-sm text-kumo-subtle">Not required to connect or publish your site.</p>
          <DnsRecordCard domain={domain} record={discovery} />
        </section>
      )}
    </div>
  );
}

function DnsRecordCard({ domain, record }: { domain: DomainSummary; record: DnsRecord }) {
  const check = record.check;
  const conflictingAAAA =
    record.type === 'A' && record.check && record.check.status !== 'LookupFailed'
      ? record.check.conflictingAAAA
      : [];
  const labels = { Matched: 'Verified', Mismatched: 'Doesn’t match yet', LookupFailed: 'Couldn’t check DNS' };
  return (
    <section
      key={`${record.type}:${record.name}`}
      aria-label={`${record.type} ${record.name}`}
      className="grid gap-3 min-w-0 rounded-lg border border-kumo-line p-4"
    >
      <p className="font-semibold">{record.type}</p>
      <dl className="m-0 grid gap-3 min-w-0">
        <CopyField type={record.type} label="Type" value={record.type} />
        <CopyField type={record.type} label="Host / Name" value={shortDnsName(record.name, domain.domain)}>
          <div className="mt-1 text-sm text-kumo-subtle wrap-anywhere">
            If your provider asks for the full name: <code className="select-text">{record.name}</code>
          </div>
        </CopyField>
        {dnsValueFields(record).map((field) => (
          <CopyField key={field.label} type={record.type} {...field} />
        ))}
      </dl>
      {record.type === 'SVCB' && (
        <p className="text-sm text-kumo-subtle">
          Use the separate fields or the complete value, depending on your provider.
        </p>
      )}
      <div className="mt-2 grid gap-1 text-sm">
        {record.type === 'TXT' && domain.status === 'Active' ? (
          <p className="font-medium">Ownership verified during setup.</p>
        ) : (
          <>
            <p className="flex items-center gap-2 font-medium">
              {check && check.status !== 'Matched' && (
                <span aria-hidden="true" className="size-1.5 shrink-0 rounded-full bg-amber-500" />
              )}
              {check ? labels[check.status] : 'Not checked yet'}
            </p>
            {check?.status === 'Mismatched' && (
              <p className="text-kumo-subtle wrap-anywhere">
                {check.observed.length ? (
                  <>
                    Currently found:{' '}
                    <code className="font-mono whitespace-pre-wrap select-text">
                      {check.observed.join(', ')}
                    </code>
                  </>
                ) : (
                  'No record found for this name.'
                )}
              </p>
            )}
            {check?.status === 'LookupFailed' && (
              <p className="text-kumo-subtle">Try checking again shortly.</p>
            )}
          </>
        )}
        {conflictingAAAA.length > 0 && (
          <p role="alert" className="text-kumo-warning wrap-anywhere">
            Remove the conflicting AAAA {conflictingAAAA.length === 1 ? 'record' : 'records'}:{' '}
            <code className="font-mono whitespace-pre-wrap select-text">{conflictingAAAA.join(', ')}</code>
          </p>
        )}
      </div>
    </section>
  );
}
